import React, { useEffect, useMemo, useRef, useState } from 'react'

import styles from './_customize-table-columns.module.scss'
import {
  Button,
  FormFooter,
  Icon,
  MultiSelect,
  SideDrawer,
} from '../../../module'
import { c } from '../../../translations/LibraryTranslationService'

/**
 * @description Component to customize table columns
 * @param title - Display title for the table settings sideDrawer
 * @param list - Array of all the possible table columns that can be displayed
 * @param selected - Array of user selected columns that will be displayed in the table
 * @param callout - Function to update/save the array of selected columns after customization has been confirmed
 * @param setToDefaultCallout - Function to be called when the "Reset" button is used (generally used to reset selected column array to default)
 * @param topChildren - custom component to be displayed above the columns selection list
 * @param bottomChildren - custom component to be displayed below the columns selection list
 * @param cancelCallout - Function to be called when the "Cancel" button is used (generally used to cancel modified settings)
 */
export type CustomizeTableColumnsProps = {
  /** Custom title for table settings */
  title?: string
  /** Array of all the columns that can be displayed in the UI */
  list: string[]
  /** Array of user selected columns that we want displayed in the UI */
  selected: string[]
  /** Updates the array of selected columns after customization has been confirmed */
  callout: (selectedList: string[]) => void
  /** The reset function to set the columns back to the default setting */
  setToDefaultCallout: () => void
  /** Children above the columns list */
  topChildren?: React.ReactNode
  /** Children below the columns list */
  bottomChildren?: React.ReactNode
  /** Function to cancel modified settings */
  cancelCallout?: () => void
  /** Optional prop to enable column reordering */
  isColumnsReorderable?: boolean
  /** Optional function to generate a secondary option (ReactNode) for display */
  getSecondaryOption?: (label: string) => React.ReactNode
}

const CustomizeTableColumns = ({
  title,
  list,
  selected,
  callout,
  setToDefaultCallout,
  topChildren,
  bottomChildren,
  cancelCallout,
  isColumnsReorderable,
  getSecondaryOption,
}: CustomizeTableColumnsProps): React.JSX.Element => {
  const [open, setOpen] = useState(false),
    [selectedColumns, setSelectedColumns] = useState(selected),
    [resetSearch, setResetSearch] = useState(false),
    topChildrenRef = useRef<HTMLDivElement>(null),
    bottomChildrenRef = useRef<HTMLDivElement>(null)

  const closeDrawer = () => {
    setOpen(false)
    setSelectedColumns(selected)
  }

  const resetButtonProps = {
    onClick: () => {
      setToDefaultCallout()
      setResetSearch(true)
    },
    children: c('setToDefault'),
  }

  useEffect(() => {
    if (open) {
      setSelectedColumns(selected)
      setResetSearch(false)
    }
  }, [open, selected])

  const multiSelectOptions = useMemo(() => {
    return list.map((item) => ({
      label: item,
      ...(getSecondaryOption
        ? { secondaryOption: getSecondaryOption(item) }
        : {}),
    }))
  }, [list, getSecondaryOption])

  const selectedMultiSelectOptions = useMemo(() => {
    return selectedColumns
      .filter((option) => list.includes(option))
      .map((item) => ({
        label: item,
        ...(getSecondaryOption
          ? { secondaryOption: getSecondaryOption(item) }
          : {}),
      }))
  }, [list, selectedColumns, getSecondaryOption])

  return (
    <>
      <Button as='unstyled' onClick={() => setOpen(true)}>
        <Icon icon='settings' color='dark-blue' />
      </Button>
      <SideDrawer
        isOpen={open}
        closeCallout={closeDrawer}
        headerContent={title ? title : c('tableSettings')}
        footerContent={({ close }) => (
          <FormFooter
            resetButtonProps={resetButtonProps}
            cancelButtonProps={{
              onClick: () => {
                close()
                setSelectedColumns(selected)
                cancelCallout?.()
                setResetSearch(true)
              },
            }}
            saveButtonProps={{
              onClick: () => {
                callout(selectedColumns)
                close()
                setResetSearch(true)
              },
              children: c('saveChanges'),
            }}
          />
        )}
      >
        {({ height }) => (
          <div className={styles.settingsWrapper}>
            {topChildren && (
              <>
                <div ref={topChildrenRef}>{topChildren}</div>
                <div className={styles.horizontalDivider} />
              </>
            )}
            <MultiSelect
              labelKey='label'
              options={multiSelectOptions}
              selectedOptions={selectedMultiSelectOptions}
              callout={(selectedOptions) => {
                setSelectedColumns(
                  selectedOptions.map((option) => option['label']),
                )
              }}
              formLabelProps={{
                label: c('tableColumns'),
              }}
              searchBarProps={{
                placeholder: c('unselectedColumns'),
                value: '',
              }}
              emptyStateProps={{
                primaryText: c('noOptionsAvailable'),
                secondaryText: c('updateSearchField'),
              }}
              maxHeight={
                height -
                24 - // 24px is the height of the label and margin of the label combined
                38 - //38px will account for the height of the top and bottom gradients
                (topChildrenRef.current?.clientHeight ?? 0) - // This is needed if there are children above the columns list
                (bottomChildrenRef.current?.clientHeight ?? 0) - // This is needed if there are children below the columns list
                (topChildrenRef.current ? 32 + 2 : 0) - // 32px is the height of the gap in between the top children and the columns list. 2px accounts for border widths
                (bottomChildrenRef.current ? 32 + 2 : 0) // 32px is the height of the gap in between the bottom children and the columns list. 2px accounts for border widths
              }
              exposed
              resetSearch={resetSearch}
              isReorderable={isColumnsReorderable}
              sortAlphabetically={false}
            />
            {bottomChildren && (
              <>
                <div className={styles.horizontalDivider} />
                <div ref={bottomChildrenRef}>{bottomChildren}</div>
              </>
            )}
          </div>
        )}
      </SideDrawer>
    </>
  )
}

export default CustomizeTableColumns
